import { useStores } from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { ComposerBanner } from '@/components/blocks/Thread/ComposerBanner';
import { XStack } from '@/components/ui';
import { PLACEHOLDER_MEETING_TITLE } from '@/features/meetings/meetings.types';

import { useSessionTitle } from './context-chat.hooks';

import type { ContextChatSession } from './context-chat.types';

interface ContextChatBannerProps {
  session: ContextChatSession;
}

/**
 * Informational banner above the Composer showing the chat's meeting
 * context. Visible only on fresh drafts so the user sees what context
 * will be sent with their first message.
 */
export const ContextChatBanner = observer(function ContextChatBanner({
  session,
}: ContextChatBannerProps) {
  const { threads } = useStores();
  const thread = threads.getById(session.threadId);

  const shouldShow =
    !thread?.isLegacyChat &&
    !thread?.synced &&
    !thread?.lastMessageId &&
    session.context.type === 'meeting';

  return (
    <AnimatePresence initial={false}>
      {shouldShow && (
        <motion.div
          key="banner"
          initial={{ y: '100%', opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: '100%', opacity: 0 }}
          transition={{ duration: 0.15, ease: 'easeOut' }}
          className="absolute left-4 right-4 bottom-[calc(100%_-_0.75rem)] -z-10"
        >
          <ComposerBanner className="!mb-0">
            <XStack className="min-w-0 items-center gap-3">
              <Icons.meetings className="size-3.5 shrink-0 text-foreground-muted" />
              <MeetingLabel context={session.context} />
            </XStack>
          </ComposerBanner>
        </motion.div>
      )}
    </AnimatePresence>
  );
});

const MeetingLabel = observer(function MeetingLabel({
  context,
}: {
  context: ContextChatSession['context'];
}) {
  const title = useSessionTitle(context);
  return (
    <span className="truncate text-sm text-foreground-subtle">
      {title || PLACEHOLDER_MEETING_TITLE}
    </span>
  );
});
